import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Form,
    Card,
    Button,
    Row,
    Col,
    Badge,
    Alert,
    InputGroup,
} from "react-bootstrap";
import { ordersApi } from "../services/services";
import { CreateOrderDto } from "../types";
import PageHeader from "../components/shared/PageHeader";
import PageContainer from "../components/shared/PageContainer";
import Flatpickr from "react-flatpickr";

const branchOptions = [
    { key: "rochdale", text: "Rochdale Branch", value: "Rochdale Branch" },
    { key: "manchester", text: "Manchester Branch", value: "Manchester Branch" },
];
const shapeOptions = [
    { key: "round", text: "Round" },
    { key: "square", text: "Square" },
    { key: "rectangle", text: "Rectangle" },
    { key: "heart", text: "Heart" },
];
const creamOptions = [
    { key: "butter", text: "Butter Cream" },
    { key: "fresh", text: "Fresh Cream" },
    { key: "chocolate", text: "Chocolate Cream" },
    { key: "fondant", text: "Fondant" },
];
const occasionOptions = [
    { key: "birthday", text: "Birthday" },
    { key: "wedding", text: "Wedding" },
    { key: "anniversary", text: "Anniversary" },
    { key: "baby_shower", text: "Baby Shower" },
    { key: "graduation", text: "Graduation" },
    { key: "other", text: "Other" },
];
const genderOptions = [
    { key: "male", text: "Male" },
    { key: "female", text: "Female" },
    { key: "neutral", text: "Neutral" },
];

interface OrderFormData {
    branch: string;
    requiredDate: Date | null;
    requiredTime: Date | null;
    collectFrom: string;
    size: string;
    quantity: number;
    shape: string;
    creamType: string;
    occasion: string;
    gender: string;
    code: string;
    messageOnCake: string;
    notes: string;
    customerName: string;
    contactNumber: string;
    orderTakenBy: string;
    price: string;
    extra: string;
    delivery: string;
    deposit: string;
}

const NewOrder: React.FC = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState<OrderFormData>({
        branch: "Rochdale Branch",
        requiredDate: null,
        requiredTime: null,
        collectFrom: "Rochdale Branch - 157 Yorkshire Street, Rochdale OL12 0DR",
        size: "",
        quantity: 1,
        shape: "",
        creamType: "",
        occasion: "",
        gender: "",
        code: "",
        messageOnCake: "",
        notes: "",
        customerName: "",
        contactNumber: "",
        orderTakenBy: "",
        price: "",
        extra: "",
        delivery: "",
        deposit: "",
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleInputChange = (field: keyof OrderFormData, value: any) =>
        setFormData((prev) => ({ ...prev, [field]: value }));
    const handleQuantityChange = (delta: number) =>
        setFormData((prev) => ({
            ...prev,
            quantity: Math.max(1, prev.quantity + delta),
        }));
    const calculateTotal = (): number =>
        (parseFloat(formData.price) || 0) +
        (parseFloat(formData.extra) || 0) +
        (parseFloat(formData.delivery) || 0);
    const calculateBalance = (): number =>
        calculateTotal() - (parseFloat(formData.deposit) || 0);

    const handleSave = () => {
        setLoading(true);
        setError(null);
        let dateRequired = new Date().toISOString();
        if (formData.requiredDate && formData.requiredTime) {
            const date = formData.requiredDate;
            const time = formData.requiredTime;
            const combined = new Date(
                date.getFullYear(),
                date.getMonth(),
                date.getDate(),
                time.getHours(),
                time.getMinutes()
            );
            dateRequired = combined.toISOString();
        } else if (formData.requiredDate) {
            dateRequired = formData.requiredDate.toISOString();
        }

        const orderData: CreateOrderDto = {
            code: formData.code || undefined,
            type: "Celebration Cake",
            orderDate: new Date().toISOString(),
            dateRequired,
            status: "Preorder",
            notes: formData.notes || undefined,
            branch: formData.branch,
            size: formData.size || undefined,
            quantity: formData.quantity,
            shape: formData.shape || undefined,
            creamType: formData.creamType || undefined,
            occasion: formData.occasion || undefined,
            gender: formData.gender || undefined,
            messageOnCake: formData.messageOnCake || undefined,
            customerName: formData.customerName || undefined,
            contactNumber: formData.contactNumber || undefined,
            orderTakenBy: formData.orderTakenBy || undefined,
            price: parseFloat(formData.price) || undefined,
            extra: parseFloat(formData.extra) || undefined,
            delivery: parseFloat(formData.delivery) || undefined,
            deposit: parseFloat(formData.deposit) || undefined,
        };

        ordersApi
            .createOrder(orderData)
            .then(() => navigate("/orders"))
            .catch((err) => setError(err.message || "Failed to create order"))
            .finally(() => setLoading(false));
    };

    return (
        <PageContainer>
            <PageHeader title="Manage" subTitle="New Order" />
            <Card className="border-top border-3 border-primary">
                <Card.Body>
                    <h4 className="text-muted">Celebration Cake Order</h4>
                    <h5 className="text-muted mt-3">Order Details</h5>

                    <Form>
                        <Row className="mb-3">
                            <Col md={3}>
                                <Form.Label className="fw-bold">Order Status</Form.Label>
                            </Col>
                            <Col md={9}>
                                <Badge bg="success">Preorder</Badge>
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={3}>
                                <Form.Label className="fw-bold">Branch</Form.Label>
                            </Col>
                            <Col md={9}>
                                <Form.Select
                                    value={formData.branch}
                                    onChange={(e) => handleInputChange("branch", e.target.value)}
                                >
                                    {branchOptions.map((o) => (
                                        <option key={o.key} value={o.value}>
                                            {o.text}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col md={3}>
                                <Form.Label className="fw-bold">Required Date</Form.Label>
                            </Col>
                            <Col md={9}>
                                <Flatpickr
                                    className="form-control"
                                    value={formData.requiredDate || ""}
                                    onChange={([date]) => handleInputChange("requiredDate", date)}
                                    options={{ dateFormat: "Y-m-d" }}
                                />
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={3}>
                                <Form.Label className="fw-bold">Required Time</Form.Label>
                            </Col>
                            <Col md={9}>
                                <Flatpickr
                                    className="form-control"
                                    value={formData.requiredTime}
                                    onChange={([date]) => handleInputChange("requiredTime", date)}
                                    options={{
                                        enableTime: true,
                                        noCalendar: true,
                                        dateFormat: "H:i",
                                        time_24hr: true,
                                    }}
                                />
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col md={3}>
                                <Form.Label className="fw-bold">Collect From</Form.Label>
                            </Col>
                            <Col md={9}>
                                <Form.Control
                                    value={formData.collectFrom}
                                    onChange={(e) =>
                                        handleInputChange("collectFrom", e.target.value)
                                    }
                                />
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={3}>
                                <Form.Label className="fw-bold">Size</Form.Label>
                            </Col>
                            <Col md={9}>
                                <Form.Control
                                    placeholder="Enter size"
                                    value={formData.size}
                                    onChange={(e) => handleInputChange("size", e.target.value)}
                                />
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={3}>
                                <Form.Label className="fw-bold">Quantity</Form.Label>
                            </Col>
                            <Col md={9}>
                                <div className="d-flex align-items-center gap-2">
                                    <Button
                                        variant="danger"
                                        onClick={() => handleQuantityChange(-1)}
                                    >
                                        <i className="bi bi-dash"></i>
                                    </Button>
                                    <Form.Control
                                        type="number"
                                        value={formData.quantity}
                                        onChange={(e) =>
                                            handleInputChange(
                                                "quantity",
                                                parseInt(e.target.value) || 1
                                            )
                                        }
                                        className="text-center w-auto"
                                    />
                                    <Button
                                        variant="success"
                                        onClick={() => handleQuantityChange(1)}
                                    >
                                        <i className="bi bi-plus"></i>
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={3}>
                                <Form.Label className="fw-bold">Shape</Form.Label>
                            </Col>
                            <Col md={9}>
                                <Form.Select
                                    value={formData.shape}
                                    onChange={(e) => handleInputChange("shape", e.target.value)}
                                >
                                    <option value="">Select Shape</option>
                                    {shapeOptions.map((o) => (
                                        <option key={o.key} value={o.text}>
                                            {o.text}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={3}>
                                <Form.Label className="fw-bold">Cream Type</Form.Label>
                            </Col>
                            <Col md={9}>
                                <Form.Select
                                    value={formData.creamType}
                                    onChange={(e) =>
                                        handleInputChange("creamType", e.target.value)
                                    }
                                >
                                    <option value="">Select Cream</option>
                                    {creamOptions.map((o) => (
                                        <option key={o.key} value={o.text}>
                                            {o.text}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={3}>
                                <Form.Label className="fw-bold">Occasion</Form.Label>
                            </Col>
                            <Col md={9}>
                                <Form.Select
                                    value={formData.occasion}
                                    onChange={(e) =>
                                        handleInputChange("occasion", e.target.value)
                                    }
                                >
                                    <option value="">Select Occasion</option>
                                    {occasionOptions.map((o) => (
                                        <option key={o.key} value={o.text}>
                                            {o.text}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={3}>
                                <Form.Label className="fw-bold">Gender</Form.Label>
                            </Col>
                            <Col md={9}>
                                <Form.Select
                                    value={formData.gender}
                                    onChange={(e) => handleInputChange("gender", e.target.value)}
                                >
                                    <option value="">Select Gender</option>
                                    {genderOptions.map((o) => (
                                        <option key={o.key} value={o.text}>
                                            {o.text}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={3}>
                                <Form.Label className="fw-bold">Code</Form.Label>
                            </Col>
                            <Col md={9}>
                                <Form.Control
                                    placeholder="Enter code"
                                    value={formData.code}
                                    onChange={(e) => handleInputChange("code", e.target.value)}
                                />
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={3}>
                                <Form.Label className="fw-bold">Message on Cake</Form.Label>
                            </Col>
                            <Col md={9}>
                                <Form.Control
                                    placeholder="Enter message for the cake"
                                    value={formData.messageOnCake}
                                    onChange={(e) =>
                                        handleInputChange("messageOnCake", e.target.value)
                                    }
                                />
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={3}>
                                <Form.Label className="fw-bold">Notes</Form.Label>
                            </Col>
                            <Col md={9}>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    placeholder="Enter any special requests or notes"
                                    value={formData.notes}
                                    onChange={(e) => handleInputChange("notes", e.target.value)}
                                />
                            </Col>
                        </Row>

                        <h5 className="text-muted mt-4">Customer Details</h5>
                        <Row className="mb-3">
                            <Col md={3}>
                                <Form.Label className="fw-bold">Customer Name</Form.Label>
                            </Col>
                            <Col md={9}>
                                <Form.Control
                                    placeholder="Enter customer name"
                                    value={formData.customerName}
                                    onChange={(e) =>
                                        handleInputChange("customerName", e.target.value)
                                    }
                                />
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={3}>
                                <Form.Label className="fw-bold">Contact Number</Form.Label>
                            </Col>
                            <Col md={9}>
                                <Form.Control
                                    placeholder="Enter contact number"
                                    value={formData.contactNumber}
                                    onChange={(e) =>
                                        handleInputChange("contactNumber", e.target.value)
                                    }
                                />
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={3}>
                                <Form.Label className="fw-bold">Order Taken By</Form.Label>
                            </Col>
                            <Col md={9}>
                                <Form.Control
                                    placeholder="Enter staff name"
                                    value={formData.orderTakenBy}
                                    onChange={(e) =>
                                        handleInputChange("orderTakenBy", e.target.value)
                                    }
                                />
                            </Col>
                        </Row>

                        <h5 className="text-muted mt-4">Payment Details</h5>
                        <Row className="mb-3">
                            <Col md={3}>
                                <Form.Label className="fw-bold">Price</Form.Label>
                            </Col>
                            <Col md={9}>
                                <InputGroup>
                                    <InputGroup.Text>£</InputGroup.Text>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        placeholder="0.00"
                                        value={formData.price}
                                        onChange={(e) => handleInputChange("price", e.target.value)}
                                    />
                                </InputGroup>
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={3}>
                                <Form.Label className="fw-bold">Extra</Form.Label>
                            </Col>
                            <Col md={9}>
                                <InputGroup>
                                    <InputGroup.Text>£</InputGroup.Text>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        placeholder="0.00"
                                        value={formData.extra}
                                        onChange={(e) => handleInputChange("extra", e.target.value)}
                                    />
                                </InputGroup>
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={3}>
                                <Form.Label className="fw-bold">Delivery</Form.Label>
                            </Col>
                            <Col md={9}>
                                <InputGroup>
                                    <InputGroup.Text>£</InputGroup.Text>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        placeholder="0.00"
                                        value={formData.delivery}
                                        onChange={(e) =>
                                            handleInputChange("delivery", e.target.value)
                                        }
                                    />
                                </InputGroup>
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={3}>
                                <Form.Label className="fw-bold">Total</Form.Label>
                            </Col>
                            <Col md={9}>
                                <InputGroup>
                                    <InputGroup.Text>£</InputGroup.Text>
                                    <Form.Control
                                        value={calculateTotal().toFixed(2)}
                                        readOnly
                                        className="bg-light"
                                    />
                                </InputGroup>
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={3}>
                                <Form.Label className="fw-bold">Deposit</Form.Label>
                            </Col>
                            <Col md={9}>
                                <InputGroup>
                                    <InputGroup.Text>£</InputGroup.Text>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        placeholder="0.00"
                                        value={formData.deposit}
                                        onChange={(e) =>
                                            handleInputChange("deposit", e.target.value)
                                        }
                                    />
                                </InputGroup>
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={3}>
                                <Form.Label className="fw-bold">Balance</Form.Label>
                            </Col>
                            <Col md={9}>
                                <InputGroup>
                                    <InputGroup.Text>£</InputGroup.Text>
                                    <Form.Control
                                        value={calculateBalance().toFixed(2)}
                                        readOnly
                                        className="bg-light"
                                    />
                                </InputGroup>
                            </Col>
                        </Row>

                        {error && (
                            <Alert variant="danger" className="mt-3">
                                {error}
                            </Alert>
                        )}

                        <div className="d-flex gap-2 mt-4">
                            <Button variant="primary" onClick={handleSave} disabled={loading}>
                                {loading ? "Saving..." : "Save"}
                            </Button>
                            <Button
                                variant="secondary"
                                onClick={() => navigate("/orders")}
                                disabled={loading}
                            >
                                Back
                            </Button>
                        </div>
                    </Form>
                </Card.Body>
            </Card>
        </PageContainer>
    );
};

export default NewOrder;
